import React, { createContext, useContext, useState, useEffect } from "react";
import { getAuth, onAuthStateChanged, signOut } from "firebase/auth";
import { app } from "../firebase/config";
import {
  verifyEmailCredentials,
  updateEmailPassword,
} from "../services/emailAuthService";
import { getUserById } from "../utils/databaseUtils";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [authMethod, setAuthMethod] = useState(null); // 'google' or 'email'
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  const auth = getAuth(app);

  // Initialize authentication state
  useEffect(() => {
    const initializeAuth = async () => {
      console.log("🚀 AuthContext initializing...");
      setIsLoading(true);

      // Check for existing email user session
      const storedUserId = localStorage.getItem("userId");
      const storedAuthMethod = localStorage.getItem("authMethod");

      console.log("🔍 Stored session data:", {
        storedUserId,
        storedAuthMethod,
      });

      if (storedUserId && storedAuthMethod === "email") {
        try {
          console.log("🔄 Verifying stored email user session:", storedUserId);
          // Verify email user still exists and is active
          const result = await getUserById(storedUserId);
          console.log("🔍 getUserById result:", result);

          if (result.success && result.user) {
            // Check if it's an email user and is active
            const isEmailUser = result.user.emailAuth ? true : false;
            const isActive = isEmailUser
              ? result.user.emailAuth.isActive
              : false;

            console.log("🔍 Email user validation:", {
              isEmailUser,
              isActive,
              hasEmailAuth: !!result.user.emailAuth,
            });

            if (isEmailUser && isActive) {
              setUser(result.user);
              setAuthMethod("email");
              // Ensure token is set for App.js compatibility
              if (!localStorage.getItem("userAccessToken")) {
                localStorage.setItem("userAccessToken", "email_auth_token");
                localStorage.setItem(
                  "userAccessTokenExpiry",
                  Date.now() + 24 * 60 * 60 * 1000
                );
              }
              console.log("✅ Email user session restored successfully");
            } else {
              console.log(
                "❌ Email user is inactive or not found, clearing session"
              );
              // Clear invalid session
              localStorage.removeItem("userId");
              localStorage.removeItem("authMethod");
              localStorage.removeItem("userAccessToken");
              localStorage.removeItem("userAccessTokenExpiry");
            }
          } else {
            console.log("❌ Failed to get user data, clearing session");
            // Clear invalid session
            localStorage.removeItem("userId");
            localStorage.removeItem("authMethod");
            localStorage.removeItem("userAccessToken");
            localStorage.removeItem("userAccessTokenExpiry");
          }
        } catch (error) {
          console.error("❌ Error verifying email user session:", error);
          localStorage.removeItem("userId");
          localStorage.removeItem("authMethod");
          localStorage.removeItem("userAccessToken");
          localStorage.removeItem("userAccessTokenExpiry");
        }
      }

      // Listen for Google authentication changes
      const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
        console.log("🔍 Firebase Auth state changed:", !!firebaseUser);

        if (firebaseUser) {
          // Google user authenticated
          try {
            const result = await getUserById(firebaseUser.uid);
            if (result.success && result.user) {
              // Only set Google user if we don't already have an email user
              const currentAuthMethod = localStorage.getItem("authMethod");
              if (currentAuthMethod !== "email") {
                setUser(result.user);
                setAuthMethod("google");
                localStorage.setItem("userId", firebaseUser.uid);
                localStorage.setItem("authMethod", "google");
                console.log("✅ Google user session set");
              }
            }
          } catch (error) {
            console.error("Error getting Google user data:", error);
          }
        } else {
          // No Google user, only clear if current method is Google
          const currentAuthMethod = localStorage.getItem("authMethod");
          if (currentAuthMethod === "google") {
            console.log("🔄 Clearing Google user session");
            setUser(null);
            setAuthMethod(null);
            localStorage.removeItem("userId");
            localStorage.removeItem("authMethod");
            localStorage.removeItem("userAccessToken");
            localStorage.removeItem("userAccessTokenExpiry");
          }
        }
      });

      setIsLoading(false);
      setIsInitialized(true);

      return unsubscribe;
    };

    initializeAuth();
  }, [auth]); // Remove authMethod dependency to prevent infinite loop

  // Debug effect to track user state changes
  useEffect(() => {
    console.log("🔄 AuthContext state changed:", {
      user: !!user,
      userId: user?.uid,
      authMethod,
      isInitialized,
      isLoading,
    });
  }, [user, authMethod, isInitialized, isLoading]);

  // Login with email/password
  const loginWithEmail = async (email, password) => {
    try {
      setIsLoading(true);

      console.log("🔄 AuthContext: Starting email login for:", email);
      const result = await verifyEmailCredentials(email, password);
      console.log("🔍 AuthContext: verifyEmailCredentials result:", {
        success: result.success,
        hasUser: !!result.user,
        error: result.error,
      });

      if (result.success && result.user) {
        setUser(result.user);
        setAuthMethod("email");
        localStorage.setItem("userId", result.user.uid);
        localStorage.setItem("authMethod", "email");
        // Set a dummy token for email users to match App.js expectations
        localStorage.setItem("userAccessToken", "email_auth_token");
        localStorage.setItem(
          "userAccessTokenExpiry",
          Date.now() + 24 * 60 * 60 * 1000
        ); // 24 hours

        console.log("✅ Email login successful in AuthContext:", {
          userId: result.user.uid,
          authMethod: "email",
          hasToken: !!localStorage.getItem("userAccessToken"),
          userState: !!user,
          authMethodState: authMethod,
        });

        // Force a small delay to ensure state is properly updated
        setTimeout(() => {
          console.log("🔍 AuthContext state after email login:", {
            user: !!user,
            authMethod,
            isAuthenticated: isAuthenticated(),
          });
        }, 50);

        return { success: true, user: result.user };
      } else {
        console.log("❌ AuthContext: Email login failed:", result.error);
        // Immediately reset loading state for failed login
        setIsLoading(false);
        // Force a small delay to ensure UI updates
        setTimeout(() => setIsLoading(false), 100);
        return {
          success: false,
          error: result.error || "Login failed. Please try again.",
        };
      }
    } catch (error) {
      console.error("❌ AuthContext: Email login error:", error);
      // Immediately reset loading state for error
      setIsLoading(false);
      // Force a small delay to ensure UI updates
      setTimeout(() => setIsLoading(false), 100);
      return { success: false, error: "Login failed. Please try again." };
    } finally {
      // Ensure loading is always reset
      setIsLoading(false);
      console.log("🔄 AuthContext: Loading state reset in finally block");
      // Additional safety reset
      setTimeout(() => {
        setIsLoading(false);
        console.log("🔄 AuthContext: Safety loading state reset");
      }, 200);
    }
  };

  // Login with Google (handled by Firebase Auth)
  const loginWithGoogle = async (firebaseUser) => {
    try {
      setIsLoading(true);

      const result = await getUserById(firebaseUser.uid);
      if (result.success && result.user) {
        setUser(result.user);
        setAuthMethod("google");
        localStorage.setItem("userId", firebaseUser.uid);
        localStorage.setItem("authMethod", "google");
        return { success: true, user: result.user };
      } else {
        return { success: false, error: "Failed to get user data" };
      }
    } catch (error) {
      console.error("Google login error:", error);
      return { success: false, error: "Login failed. Please try again." };
    } finally {
      setIsLoading(false);
    }
  };

  // Logout
  const logout = async () => {
    try {
      setIsLoading(true);

      // Sign out from Google if applicable
      if (authMethod === "google") {
        await signOut(auth);
      }

      // Clear local state and storage
      setUser(null);
      setAuthMethod(null);
      localStorage.removeItem("userId");
      localStorage.removeItem("authMethod");
      localStorage.removeItem("userAccessToken");
      localStorage.removeItem("userAccessTokenExpiry");
      localStorage.removeItem("redirecting");
      localStorage.removeItem("returnUrl");

      return { success: true };
    } catch (error) {
      console.error("Logout error:", error);
      return { success: false, error: "Logout failed" };
    } finally {
      setIsLoading(false);
    }
  };

  // Change password (email users only)
  const changePassword = async (currentPassword, newPassword) => {
    if (authMethod !== "email" || !user) {
      return {
        success: false,
        error: "Password change is only available for email users",
      };
    }

    try {
      const result = await updateEmailPassword(
        user.uid,
        currentPassword,
        newPassword
      );
      return result;
    } catch (error) {
      console.error("Password change error:", error);
      return {
        success: false,
        error: "Password change failed. Please try again.",
      };
    }
  };

  // Refresh user data
  const refreshUser = async () => {
    if (!user) return { success: false, error: "No user to refresh" };

    try {
      const result = await getUserById(user.uid);
      if (result.success && result.user) {
        setUser(result.user);
        return { success: true, user: result.user };
      } else {
        return { success: false, error: "Failed to refresh user data" };
      }
    } catch (error) {
      console.error("Refresh user error:", error);
      return { success: false, error: "Failed to refresh user data" };
    }
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    const result = user !== null && authMethod !== null;
    const storedUserId = localStorage.getItem("userId");
    const storedAuthMethod = localStorage.getItem("authMethod");

    console.log("🔍 AuthContext.isAuthenticated():", {
      user: !!user,
      authMethod,
      result,
      userId: user?.uid,
      storedUserId,
      storedAuthMethod,
      isInitialized,
      isLoading,
    });
    return result;
  };

  // Check if user is Google user
  const isGoogleUser = () => {
    return authMethod === "google";
  };

  // Check if user is email user
  const isEmailUser = () => {
    return authMethod === "email";
  };

  // Get user display name
  const getUserDisplayName = () => {
    if (!user) return null;
    return (
      user.profile?.displayName || user.name || user.email || "Unknown User"
    );
  };

  // Get user email
  const getUserEmail = () => {
    if (!user) return null;
    return user.profile?.email || user.email || null;
  };

  // Get user photo URL
  const getUserPhotoURL = () => {
    if (!user) return null;
    return user.profile?.photoURL || null;
  };

  // Check if user has active membership
  const hasActiveMembership = () => {
    if (!user || !user.membership || !user.membershipExpiry) return false;

    try {
      const expiryDate = new Date(user.membershipExpiry);
      const now = new Date();
      return expiryDate > now;
    } catch {
      return false;
    }
  };

  // Get membership info
  const getMembershipInfo = () => {
    if (!user) return null;

    const membership = user.membership || 0;
    const membershipExpiry = user.membershipExpiry;

    let status = "free";
    let expiryDate = null;
    let daysRemaining = null;

    if (membership && membershipExpiry) {
      try {
        expiryDate = new Date(membershipExpiry);
        const now = new Date();

        if (expiryDate > now) {
          status = "premium";
          daysRemaining = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
        } else {
          status = "expired";
        }
      } catch {
        status = "expired";
      }
    }

    return {
      level: membership,
      status,
      expiryDate,
      daysRemaining,
    };
  };

  const value = {
    // State
    user,
    authMethod,
    isLoading,
    isInitialized,

    // Authentication methods
    loginWithEmail,
    loginWithGoogle,
    logout,

    // User management
    changePassword,
    refreshUser,

    // Utility methods
    isAuthenticated,
    isGoogleUser,
    isEmailUser,
    getUserDisplayName,
    getUserEmail,
    getUserPhotoURL,
    hasActiveMembership,
    getMembershipInfo,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
